import React, { useState, useEffect } from "react";
import styles from "./EventsUpdate.module.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_ALL_EVENTS, UPDATE_EVENT, ADD_EVENT, UPDATE_EVENT_STATUS, GET_ALL_LEAGUES, SEARCH_VENUES } from "./api/EventsUpdateQuery";

import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton
} from "@mui/material";

// MUI Icons
import SearchIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useQuery } from "@apollo/client";
import { getEventStatusDetails, getEventSyncPayload } from "./utils/eventStatus";

interface Event {
  id: string;
  eventName: string;
  displayName: string;
  venue: string;
  date: string;
  time: string;
  league: string;
  status: Boolean;
}

const EventsUpdate: React.FC = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  
  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);
  
  const [searchEventName, setSearchEventName] = useState("");
  const [headerSearch, setHeaderSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "hidden" | "tbd">("all");
  const [events, setEvents] = useState<any>({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  
  // Pagination & sorting states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columnTypes, setColumnTypes] = useState("e_id");
  const [sortType, setSortType] = useState("desc");
  const [showAllDates, setShowAllDates] = useState(true);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    eventName: "",
    displayName: "",
    eventDate: "",
    city: "",
    state: "",
    venue: "",
    eventTime: "",
    league: "",
    homeTeam: "",
    awayTeam: "",
  });
  
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    eventDate: "",
    eventTime: ""
  });
  
  const { data: leaguesData, loading: leaguesLoading } = useQuery(GET_ALL_LEAGUES);

  // Venue autocomplete state and query
  const [searchVenues, { data: venuesData, loading: venuesLoading }] = useLazyQuery(SEARCH_VENUES, {
    fetchPolicy: "network-only"
  });

  // Dark mode theme colors
  const themeColors = {
    // Text colors
    textPrimary: darkMode ? "#f1f5f9" : "#111827",
    textSecondary: darkMode ? "#94a3b8" : "#666",
    textTertiary: darkMode ? "#64748b" : "#888",
    textLabel: darkMode ? "#f1f5f9" : "#374151",
    errorText: "#dc2626",
    infoText: darkMode ? "#93c5fd" : "#3b82f6",
    
    // Background colors
    bgPrimary: darkMode ? "#1e293b" : "#ffffff",
    bgSecondary: darkMode ? "#334155" : "#f8fafc",
    bgHover: darkMode ? "#475569" : "#f3f4f6",
    bgInfoLight: darkMode ? "#1e293b" : "rgb(221 229 255)",
    
    // Border colors
    borderColor: darkMode ? "#334155" : "#e5e7eb",
    borderInput: darkMode ? "#475569" : "#d1d5db",
    
    // Button colors
    buttonBgPrimary: darkMode ? "#818cf8" : "#0f172a",
    buttonBgHover: darkMode ? "#6366f1" : "#1e293b",
    buttonTextPrimary: "#ffffff",
    
    // Special colors
    infoBoxBg: darkMode ? "#1e293b" : "rgb(221 229 255)",
    infoBoxText: darkMode ? "#93c5fd" : "rgb(29, 78, 216)"
  };

  // GraphQL Query
  const [getAllEvents] = useLazyQuery(GET_ALL_EVENTS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.events && Array.isArray(data.events)) {
        const filteredData = filterEventsByDate(data);
        setEvents(filteredData);
        if (filteredData?.events_aggregate?.aggregate?.count !== undefined) {
          setTotalCount(filteredData.events_aggregate.aggregate.count);
        }
      } else {
        setEvents({ events: [], events_aggregate: { aggregate: { count: 0 } } });
        setTotalCount(0);
      }
      setLoading(false);
    },
    onError: (err) => {
      setEvents({ events: [], events_aggregate: { aggregate: { count: 0 } } });
      setTotalCount(0);
      setLoading(false);
    },
  });

  // Mutations
  const [updateEventStatus] = useMutation(UPDATE_EVENT_STATUS, {
    onCompleted: () => {
      // Refetch with current filters – DO NOT reset search/pagination
      fetchEventsFromBackend();
      toast.dismiss();
      toast.success("Event status updated successfully!");
      // Clear selection after single update
      setSelectedIds([]);
    },
    onError: (err) => {
      console.error("Error updating event:", err);
      toast.dismiss();
      toast.error("Failed to update event status");
    },
  });

  const [addEventMutation] = useMutation(ADD_EVENT, {
    onCompleted: (data) => {
      toast.dismiss();
      toast.success("Event added successfully!");
      setOpenAddModal(false);
      setFormData({
        eventName: "",
        displayName: "",
        eventDate: "",
        city: "",
        state: "",
        venue: "",
        eventTime: "",
        league: "",
        homeTeam: "",
        awayTeam: "",
      });
      setAddLoading(false);
      setCurrentPage(1);
      setFilterStatus("all");
      setShowAllDates(true);
      setColumnTypes("e_id");
      setSortType("desc");
      setSearchEventName("");
      setHeaderSearch("");
      fetchEventsFromBackend("%%", 1);
    },
    onError: (err) => {
      console.error("Error adding event:", err);
      toast.dismiss();
      toast.error("Failed to add event. Please try again.");
      setAddLoading(false);
    },
  });

  const getGraphQLErrorMessage = (err: unknown, fallback: string) => {
    const graphQLError = (err as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message;
    return graphQLError || (err as Error)?.message || fallback;
  };

  const normalizeEventTimeForSave = (timeValue: string, existingRawTime?: string) => {
    const trimmed = timeValue?.trim();
    if (trimmed) {
      return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
    }
    if (existingRawTime) {
      const existingMatch = existingRawTime.toString().match(/^(\d{2}:\d{2}(?::\d{2})?)/);
      if (existingMatch) {
        return existingMatch[1].length === 5 ? `${existingMatch[1]}:00` : existingMatch[1];
      }
    }
    return null;
  };

  const [updateEvent] = useMutation(UPDATE_EVENT, {
    onCompleted: () => {
      toast.dismiss();
      toast.success("Event updated successfully!", {
        toastId: "event-update-success"
      });
      setOpenEditModal(false);
      setEditLoading(false);
      fetchEventsFromBackend();
    },
    onError: (err) => {
      console.error("Update error:", err);
      toast.dismiss();
      toast.error(getGraphQLErrorMessage(err, "Failed to update event"));
      setEditLoading(false);
    }
  });

  // Handle page change
  const handlePageChange = (_event: any, newPage: number) => {
    setCurrentPage(newPage);
    // Clear selection when page changes
    setSelectedIds([]);
  };

  // Clear all searches
  const handleClearAllSearches = () => {
    setHeaderSearch("");
    setSearchEventName("");
    setFilterStatus("all");
    setShowAllDates(true);
    setColumnTypes("e_id");
    setSortType("desc");
    setCurrentPage(1);
    setSelectedIds([]);
    fetchEventsFromBackend("%%", 1);
  };

  // Fetch events from backend
  const fetchEventsFromBackend = (searchTextOverride?: string, pageOverride?: number) => {
    setLoading(true);
    const searchText = searchTextOverride !== undefined ? searchTextOverride : (searchEventName ? `%${searchEventName}%` : "%%");
    const page = pageOverride ?? currentPage;
    const offset = (page - 1) * rowsPerPage;
    
    const variables = {
      limit: rowsPerPage,
      offset: offset,
      orderBy: { [columnTypes]: sortType },
      searchText: searchText,
    };

    console.log("Fetching Events with variables:", variables);
    getAllEvents({
      variables: variables,
    });
  };

  // Initial fetch
  useEffect(() => {
    console.log("Component mounted - triggering initial fetch");
    fetchEventsFromBackend();
  }, []);

  // Refetch on dependencies
  useEffect(() => {
    if (currentPage > 1 || searchEventName || columnTypes !== "e_id" || sortType !== "desc" || !showAllDates) {
      console.log("Dependencies changed - refetching");
      fetchEventsFromBackend();
    }
  }, [searchEventName, currentPage, rowsPerPage, columnTypes, sortType, showAllDates]);

  const handleRefresh = () => {
    setCurrentPage(1);
    setFilterStatus("all");
    setShowAllDates(true);
    setSearchEventName("");
    setHeaderSearch("");
    setSelectedIds([]);
    fetchEventsFromBackend("%%", 1);
  };

  // Filter events by date
  const filterEventsByDate = (eventsData: any) => {
    if (!eventsData || !eventsData.events) return eventsData;
    
    let filteredEvents = eventsData.events;
    let filteredAggregate = eventsData.events_aggregate;

    if (!showAllDates) {
      filteredEvents = eventsData.events.filter((event: any) => {
        const hasValidDate = event.e_date !== null && event.e_date !== undefined && event.e_date !== '';
        const isTBD = event.e_is_active?.toLowerCase().includes('tbd');
        const isValid = hasValidDate && !isTBD;
        return isValid;
      });
      filteredAggregate = {
        ...eventsData.events_aggregate,
        aggregate: {
          ...eventsData.events_aggregate?.aggregate,
          count: filteredEvents.length
        }
      };
    }

    return {
      ...eventsData,
      events: filteredEvents,
      events_aggregate: filteredAggregate
    };
  }

  // Helper functions to validate date and time
  const isValidDate = (dateStr: string) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return !isNaN(d.getTime());
  };

  const isValidTime = (timeStr: string) => {
    if (!timeStr) return false;
    const d = new Date(`1970-01-01T${timeStr}`);
    return !isNaN(d.getTime());
  };

  const normalizeLeagueValue = (value?: string) => {
    const normalized = value?.trim() || "";
    if (!normalized) return "";
    return normalized.toLowerCase().includes("ticketmaster") ? "" : normalized;
  };

  const getEventLeagueName = (event: any) => {
    const leagueName = normalizeLeagueValue(event?.event_categories?.[0]?.league_category?.league?.l_name);
    if (leagueName) return leagueName;

    const fallbackLeague = normalizeLeagueValue(event?.e_state);
    if (fallbackLeague) return fallbackLeague;

    return "";
  };

  // Transform raw events data
  const getLeagueBadgeClass = (league: string) => {
    const leagueUpper = league?.toUpperCase() || "";
    if (leagueUpper === "NBA") return styles.leagueNBA;
    if (leagueUpper === "NFL") return styles.leagueNFL;
    if (leagueUpper === "MLB") return styles.leagueMLB;
    if (leagueUpper === "NHL") return styles.leagueNHL;
    if (leagueUpper === "MLS") return styles.leagueMLS;
    if (leagueUpper === "WNBA") return styles.leagueWNBA;
    if (leagueUpper === "NCAAM" || leagueUpper === "NCAAW" || leagueUpper === "NCAAFB") return styles.leagueNCAA;
    if (leagueUpper === "CONCERT") return styles.leagueConcert;
    return styles.leagueDefault;
  };

  const transformedEvents: Event[] = (events?.events || []).map((event: any) => {
    const statusDetails = getEventStatusDetails(event);
    return {
      id: event.e_id,
      eventName: event.e_name || "",
      displayName: event.e_name || "",
      venue: event.e_address || event.e_city || "N/A",
      date: isValidDate(event.e_date)
        ? new Date(event.e_date).toLocaleDateString("en-US")
        : "TBD",
      rawDate: event.e_date || "",
      time: isValidTime(event.e_time)
        ? new Date(`1970-01-01T${event.e_time}`).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
        : "TBD",
      rawTime: event.e_time || "",
      league: getEventLeagueName(event),
      status: statusDetails.isActive,
      rawStatus: event.e_status || "",
      statusLabel: statusDetails.label,
      isRemoved: statusDetails.isRemoved,
    };
  });

  const selectedEvents = transformedEvents.filter((event: any) => selectedIds.includes(event.id));
  const selectedActiveCount = selectedEvents.filter((event: any) => event.status === true && !event.isRemoved).length;
  const selectedHiddenCount = selectedEvents.filter((event: any) => event.status === false || event.isRemoved).length;
  const selectedHasActive = selectedActiveCount > 0;
  const selectedHasHidden = selectedHiddenCount > 0;
  const isMixedStatusSelection = selectedHasActive && selectedHasHidden;
  const hideButtonDisabled = bulkActionLoading || selectedIds.length === 0 || !selectedHasActive || isMixedStatusSelection;
  const showButtonDisabled = bulkActionLoading || selectedIds.length === 0 || !selectedHasHidden || isMixedStatusSelection;

  // Apply both filters: header search AND filter status
  const filteredEvents = transformedEvents.filter((event: any) => {
    if (event.rawStatus?.toLowerCase() === "closed") {
      return false;
    }

    const matchesHeaderSearch = headerSearch === "" || 
      event.eventName?.toLowerCase().includes(headerSearch.toLowerCase()) ||
      event.venue?.toLowerCase().includes(headerSearch.toLowerCase()) ||
      event.league?.toLowerCase().includes(headerSearch.toLowerCase()) ||
      event.displayName?.toLowerCase().includes(headerSearch.toLowerCase());

    let matchesStatus = true;
    if (filterStatus === "active") {
      matchesStatus = event.status === true && !event.isRemoved;
    } else if (filterStatus === "hidden") {
      matchesStatus = event.status === false || event.isRemoved;
    } else if (filterStatus === "tbd") {
      matchesStatus = event.date === "TBD";
    }

    return matchesHeaderSearch && matchesStatus;
  });

  // ----- Bulk selection helpers -----
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredEvents.map(e => e.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(pid => pid !== id));
    }
  };

  const isAllSelected = filteredEvents.length > 0 && filteredEvents.every(e => selectedIds.includes(e.id));

  // Bulk actions
  const handleBulkHide = async () => {
    if (selectedIds.length === 0 || isMixedStatusSelection) {
      if (isMixedStatusSelection) {
        toast.info("Selected events contain different statuses. Please select only Active events to hide.");
      }
      return;
    }
    setBulkActionLoading(true);
    try {
      for (const id of selectedIds) {
        await updateEventStatus({
          variables: { e_id: id, ...getEventSyncPayload(false) }
        });
      }
      fetchEventsFromBackend();
      setSelectedIds([]);
      toast.success(`Successfully hid ${selectedIds.length} event(s)`);
    } catch (err) {
      toast.error("Bulk hide failed. Please try again.");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkShow = async () => {
    if (selectedIds.length === 0 || isMixedStatusSelection) {
      if (isMixedStatusSelection) {
        toast.info("Selected events contain different statuses. Please select only Hidden events to show.");
      }
      return;
    }
    setBulkActionLoading(true);
    try {
      for (const id of selectedIds) {
        await updateEventStatus({
          variables: { e_id: id, ...getEventSyncPayload(true) }
        });
      }
      fetchEventsFromBackend();
      setSelectedIds([]);
      toast.success(`Successfully showed ${selectedIds.length} event(s)`);
    } catch (err) {
      toast.error("Bulk show failed. Please try again.");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Single hide/show functions (unchanged but now they also clear selection)
  const handleHide = (id: string) => {
    updateEventStatus({
      variables: { e_id: id, ...getEventSyncPayload(false) }
    });
  };
  
  const handleShow = (id: string) => {
    updateEventStatus({
      variables: { e_id: id, ...getEventSyncPayload(true) }
    });
  };

  const todayDate = new Date().toISOString().split("T")[0];
  const currentTime = new Date()
    .toTimeString()
    .slice(0, 5);
  const handleAddEvent = () => {
    if (
      !formData.eventName.trim() ||
      !formData.eventDate ||
      !formData.city.trim() ||
      !formData.state.trim() ||
      !formData.venue.trim() ||
      !formData.eventTime ||
      !formData.league.trim()
    ) {
      toast.dismiss();
      toast.warning("Please fill all required fields");
      return;
    }

    setAddLoading(true);

    addEventMutation({
      variables: {
        eventInput: {
          e_name: formData.eventName,
          e_date: formData.eventDate,
          e_time: formData.eventTime,
          e_city: formData.city,
          e_state: formData.state,
          e_address: formData.venue,
          e_platform_name: formData.league,
          e_is_active: true,
          e_status: "Active"
        }
      }
    });
  };

  const handleCloseModal = () => {
    setOpenAddModal(false);
    setFormData({
      eventName: "",
      displayName: "",
      eventDate: "",
      city: "",
      state: "",
      venue: "",
      eventTime: "",
      league: "",
      homeTeam: "",
      awayTeam: "",
    });
  };

  const handleSaveEdit = async () => {
    if (!selectedEvent) return;

    if (!editFormData.displayName.trim()) {
      toast.warning("Display name is required");
      return;
    }
    if (!editFormData.eventDate) {
      toast.warning("Event date is required");
      return;
    }

    const normalizedTime = normalizeEventTimeForSave(
      editFormData.eventTime,
      selectedEvent.rawTime,
    );

    setEditLoading(true);

    try {
      await updateEvent({
        variables: {
          e_id: selectedEvent.id,
          eventData: {
            e_name: editFormData.displayName.trim(),
            e_date: editFormData.eventDate,
            e_time: normalizedTime,
          }
        }
      });
    } catch (error) {
      console.error("Update Error:", error);
      toast.dismiss();
      toast.error(getGraphQLErrorMessage(error, "Failed to update event"));
      setEditLoading(false);
    }
  };
  
  const handleEdit = (event: any) => {
    console.log(event);
    setSelectedEvent(event);

    let eventDate = "";
    let eventTime = "";

    if (event.rawDate) {
      const normalizedDate = event.rawDate.toString().split("T")[0].split(" ")[0];
      const dateMatch = normalizedDate.match(/^(\d{4}-\d{2}-\d{2})$/);
      if (dateMatch) {
        eventDate = dateMatch[1];
      }
    }

    if (event.rawTime) {
      const timeMatch = event.rawTime.toString().match(/^(\d{2}:\d{2})/);
      if (timeMatch) {
        eventTime = timeMatch[1];
      } else {
        const timeValue = new Date(`1970-01-01T${event.rawTime}`);
        if (!isNaN(timeValue.getTime())) {
          eventTime = timeValue.toISOString().split("T")[1].substring(0, 5);
        }
      }
    }

    setEditFormData({
      displayName: event.displayName,
      eventDate,
      eventTime
    });

    setOpenEditModal(true);
  };

  const sortedEvents = filteredEvents;
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <div className={`${styles.container} ${darkMode ? styles.darkMode : ""}`}>
      {/* HEADER */}
      <div className={styles.header}>
        <div className={styles.searchSection}>
          <div>
            <p className={styles.subtitle}>Manage Event Dates & Times</p>
            <h1 className={styles.title}>Events Update</h1>
          </div>
        </div>
        <div style={{display:"flex",gap:"10px"}}>
          <div className={styles.iconGroup}>
            <button 
              className={styles.iconBtn}
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshRoundedIcon style={{ opacity: loading ? 0.5 : 1 }} />
            </button>
          
            <button
              className={styles.iconBtn}
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </button>
          </div>
          <button 
            className={styles.addBtn}
            onClick={() => setOpenAddModal(true)}
          >
            + Add Event
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className={styles.card}>
        <div className={styles.filterRow}>
          <div className={styles.searchWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by event name..."
              value={searchEventName}
              onChange={(e) => setSearchEventName(e.target.value)}
            />
            {searchEventName && (
              <button
                type="button"
                onClick={handleClearAllSearches}
                className={styles.clearSearchBtn}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6b7280",
                  fontSize: "16px",
                  zIndex: 10
                }}
              >
                ✕
              </button>
            )}
          </div>
          
          <div className={styles.filterTabs}>
            <button
              className={`${styles.filtersBtn} ${
                filterStatus === "tbd" ? styles.otherFilter + " " + styles.active : ""
              }`}
              onClick={() => setFilterStatus("tbd")}
            >
              <WarningAmberRoundedIcon fontSize="small" />
              Show TBD Only
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 15px", borderRadius: "10px",boxShadow: "1px 1px 4px 1px #e4e4e4;" }}>
              <button
                className={`${styles.filterBtn} ${
                  filterStatus === "active" ? styles.activeFilter : ""
                }`}
                onClick={() => setFilterStatus("active")}
              >
                Active
              </button>

              <button
                className={`${styles.filterBtn} ${
                  filterStatus === "hidden" ? styles.hiddenFilter : ""
                }`}
                onClick={() => setFilterStatus("hidden")}
              >
                Hidden
              </button>

              <button
                className={`${styles.filterBtn} ${
                  filterStatus === "all" ? styles.allFilter + " " + styles.active : ""
                }`}
                onClick={() => setFilterStatus("all")}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className={styles.tableCard}>
        {/* Bulk Actions Toolbar */}
        {selectedIds.length > 0 && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "14px 18px",
            backgroundColor: darkMode ? "#1f2937" : "#f8fafc",
            borderRadius: "14px",
            marginBottom: "16px",
            border: `1px solid ${themeColors.borderColor}`,
            boxShadow: darkMode ? "0 10px 30px rgba(15,23,42,0.2)" : "0 10px 30px rgba(15,23,42,0.06)",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={{ fontWeight: 600, color: themeColors.textPrimary }}>
                {selectedIds.length} event{selectedIds.length > 1 ? 's' : ''} selected
              </span>
              <span style={{ fontSize: "12px", color: themeColors.textSecondary }}>
                {isMixedStatusSelection
                  ? "Mixed statuses selected — use only one type at a time."
                  : selectedHasActive
                    ? "Ready to hide active event(s)."
                    : selectedHasHidden
                      ? "Ready to show hidden event(s)."
                      : "Select events to perform bulk actions."}
              </span>
            </div>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkHide}
              disabled={hideButtonDisabled}
              startIcon={<VisibilityOffOutlinedIcon />}
              sx={{
                textTransform: "none",
                borderColor: "#dc2626",
                color: "#dc2626",
                borderRadius: "10px",
                fontWeight: 600,
                minWidth: "148px",
                "&:hover": { borderColor: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }
              }}
            >
              Hide Selected
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkShow}
              disabled={showButtonDisabled}
              startIcon={<VisibilityOutlinedIcon />}
              sx={{
                textTransform: "none",
                borderColor: "#16a34a",
                color: "#16a34a",
                borderRadius: "10px",
                fontWeight: 600,
                minWidth: "148px",
                "&:hover": { borderColor: "#15803d", backgroundColor: "rgba(22,163,74,0.08)" }
              }}
            >
              Show Selected
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedIds([])}
              disabled={bulkActionLoading}
              sx={{
                textTransform: "none",
                color: themeColors.textSecondary,
                minWidth: "116px",
              }}
            >
              Clear Selection
            </Button>
            {bulkActionLoading && <CircularProgress size={20} />}
          </div>
        )}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress />
          </div>
        ) : sortedEvents.length === 0 ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            textAlign: "center",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            minHeight: "400px"
          }}>
            <h2 style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1f2937",
              margin: "0 0 8px 0"
            }}>
              No Events Found
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              {headerSearch || searchEventName 
                ? `No events match your search criteria.` 
                : "No events available."}
            </p>
            {(headerSearch || searchEventName) && (
              <button
                onClick={handleClearAllSearches}
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  background: "#5D75F8",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px"
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={selectedIds.length > 0 && !isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={loading || bulkActionLoading}
                      sx={{
                        color: themeColors.textSecondary,
                        '&.Mui-checked': { color: themeColors.buttonBgPrimary },
                      }}
                    />
                  </th>
                  <th>EVENT NAME</th>
                  <th>DISPLAY NAME</th>
                  <th>VENUE</th>
                  <th>DATE</th>
                  <th>TIME</th>
                  <th>LEAGUE</th>
                  <th>STATUS</th>
                  <th className={styles.actionCol}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event: any) => {
                  // Defensive time rendering
                  let displayTime = "TBD";
                  if (event.rawTime && event.rawTime !== "TBD") {
                    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/;
                    if (timePattern.test(event.rawTime)) {
                      const d = new Date(`1970-01-01T${event.rawTime}`);
                      if (!isNaN(d.getTime())) {
                        displayTime = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
                      }
                    }
                  }
                  return (
                    <tr key={event.id}>
                      <td>
                        <Checkbox
                          checked={selectedIds.includes(event.id)}
                          onChange={(e) => handleSelectOne(event.id, e.target.checked)}
                          disabled={loading || bulkActionLoading}
                          sx={{
                            color: themeColors.textSecondary,
                            '&.Mui-checked': { color: themeColors.buttonBgPrimary },
                          }}
                        />
                      </td>
                      <td className={styles.eventName}>{event.eventName}</td>
                      <td>{event.displayName}</td>
                      <td className={styles.venue}>{event.venue}</td>
                      <td className={styles.date}>{event.date}</td>
                      <td className={styles.time}>{displayTime}</td>
                      <td className={styles.league}>
                        <span className={`${styles.leagueBadge} ${getLeagueBadgeClass(event.league || "")}`}>
                          {event.league || "Unknown"}
                        </span>
                      </td>
                      <td>
                        {event.status ? (
                          <span className={styles.statusActive}>Active</span>
                        ) : (
                          <span className={styles.statusInactive}>{event.statusLabel || 'Hidden'}</span>
                        )}
                      </td>
                      <td className={styles.actionCol}>
                        <div className={styles.actionBtns}>
                          <button className={styles.editBtn} onClick={() => handleEdit(event)}>
                            <EditOutlinedIcon fontSize="small" /> Edit
                          </button>
                          {event.status ? (
                            <button
                              className={styles.hideBtn}
                              onClick={() => handleHide(event.id)}
                            >
                              <VisibilityOffOutlinedIcon fontSize="small" /> Hide
                            </button>
                          ) : (
                            <button
                              className={styles.ShowBtn}
                              onClick={() => handleShow(event.id)}
                            >
                              <VisibilityOutlinedIcon fontSize="small" /> Show
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* PAGINATION */}
            <div className={styles.tableFooter}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <div style={{ color: "#666", fontSize: "14px" }}>
                  Showing {sortedEvents.length} of {totalCount} events
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </span>
                  <button
                    onClick={() => handlePageChange(null, currentPage - 1)}
                    disabled={currentPage === 1 || loading || sortedEvents.length === 0}
                    style={{
                      padding: "8px 14px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      cursor: (currentPage === 1 || sortedEvents.length === 0) ? "not-allowed" : "pointer",
                      opacity: (currentPage === 1 || sortedEvents.length === 0) ? 0.5 : 1,
                      backgroundColor: "#fff",
                      fontWeight: "500",
                      fontSize: "13px",
                      color: "#333",
                    }}
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => {
                      const maxPage = Math.ceil(totalCount / rowsPerPage);
                      if (currentPage < maxPage) {
                        handlePageChange(null, currentPage + 1);
                      }
                    }}
                    disabled={currentPage >= totalPages || loading || sortedEvents.length === 0}
                    style={{
                      padding: "8px 14px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      cursor: (currentPage >= totalPages || sortedEvents.length === 0) ? "not-allowed" : "pointer",
                      opacity: (currentPage >= totalPages || sortedEvents.length === 0) ? 0.5 : 1,
                      backgroundColor: "#fff",
                      fontWeight: "500",
                      fontSize: "13px",
                      color: "#333",
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* ADD EVENT MODAL */}
      <Dialog 
        open={openAddModal} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleCloseModal();
        }} 
        maxWidth="sm" 
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: darkMode ? "0 20px 25px -5px rgba(0, 0, 0, 0.5)" : "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            transition: "all 0.3s ease"
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderBottom: `1px solid ${themeColors.borderColor}`,
            pb: 2,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            flexShrink: 0,
            margin: 0,
            transition: "all 0.3s ease"
          }}
        >
          Add New Event
          <IconButton
            onClick={handleCloseModal}
            sx={{ ml: "auto", p: 0.5, color: themeColors.textSecondary }}
            disabled={addLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: "14px", 
            pt: 2.5,
            overflowY: "auto",
            flex: 1,
            backgroundColor: themeColors.bgPrimary,
            transition: "all 0.3s ease"
          }}
        >
          <p style={{ color: themeColors.textSecondary, fontSize: "13px", margin: "0 0 8px 0", fontWeight: 500, transition: "color 0.3s ease" }}>
            Enter the event details below. All fields marked <span style={{ color: "#dc2626", fontWeight: 600 }}>*</span> are required.
          </p>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
              Event Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              min={todayDate}
              type="text"
              value={formData.eventName}
              onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
              placeholder="e.g., Sugar Bowl — Texas vs Ohio State"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${themeColors.borderInput}`,
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "inherit",
                boxSizing: "border-box",
                backgroundColor: themeColors.bgSecondary,
                color: themeColors.textPrimary,
                transition: "all 0.3s ease"
              }}
            />
          </div>
          
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
              Display Name (optional - for team announcements)
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Sugar Bowl — Texas vs Ohio State"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${themeColors.borderInput}`,
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "inherit",
                boxSizing: "border-box",
                backgroundColor: themeColors.bgSecondary,
                color: themeColors.textPrimary,
                transition: "all 0.3s ease"
              }}
            />
            <p style={{ color: themeColors.infoText, fontSize: "12px", margin: "6px 0 0 0", fontWeight: 400, transition: "color 0.3s ease" }}>
              Leave blank to use Event Name initially. Update later when teams are announced.
            </p>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
              Event Date <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                min={todayDate}
                value={formData.eventDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    eventDate: e.target.value
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  paddingRight: "36px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  transition: "all 0.3s ease"
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
                City <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="e.g., New Orleans"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  transition: "all 0.3s ease"
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
                State <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="e.g., LA"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  transition: "all 0.3s ease"
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
              Venue Name (searchable) <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Autocomplete
              freeSolo
              options={(venuesData?.events || []).map((v: any) => v.e_address).filter(Boolean)}
              loading={venuesLoading}
              value={formData.venue}
              onInputChange={(_e, value) => {
                setFormData({ ...formData, venue: value });
                if (value && value.length >= 2) {
                  searchVenues({ variables: { searchText: `%${value}%` } });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Venue Name (searchable)"
                  placeholder="Search or type venue name..."
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: themeColors.textLabel,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: themeColors.buttonBgPrimary,
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: themeColors.bgSecondary,
                      '& fieldset': {
                        borderColor: themeColors.borderInput,
                      },
                      '&:hover fieldset': {
                        borderColor: themeColors.borderInput,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: themeColors.buttonBgPrimary,
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: themeColors.textPrimary,
                    },
                    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                      color: themeColors.textSecondary,
                    },
                  }}
                />
              )}
            />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
                Event Time <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="time"
                min={
                  formData.eventDate === todayDate
                    ? currentTime
                    : undefined
                }
                value={formData.eventTime}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    eventTime: e.target.value
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
                League <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <select
                value={formData.league}
                onChange={(e) => setFormData({ ...formData, league: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  cursor: "pointer"
                }}
              >
                <option value="">Select league</option>
                {leaguesLoading ? (
                  <option>Loading...</option>
                ) : (
                  leaguesData?.leagues?.map((league: any) => (
                    <option key={league.l_id} value={league.l_name}>
                      {league.l_name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Home Team and Away Team */}
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
                Home Team (optional)
              </label>
              <input
                type="text"
                value={formData.homeTeam}
                onChange={(e) => setFormData({ ...formData, homeTeam: e.target.value })}
                placeholder="Enter home team name"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: themeColors.textLabel, marginBottom: "6px", transition: "color 0.3s ease" }}>
                Away Team (optional)
              </label>
              <input
                type="text"
                value={formData.awayTeam}
                onChange={(e) => setFormData({ ...formData, awayTeam: e.target.value })}
                placeholder="Enter away team name"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary
                }}
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions 
          sx={{ 
            padding: "16px", 
            gap: "10px", 
            borderTop: `1px solid ${themeColors.borderColor}`,
            position: "sticky",
            bottom: 0,
            backgroundColor: themeColors.bgPrimary,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Button 
            onClick={handleCloseModal} 
            disabled={addLoading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: themeColors.textSecondary,
              border: `1px solid ${themeColors.borderInput}`,
              "&:hover": {
                backgroundColor: themeColors.bgHover
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddEvent}
            disabled={addLoading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: themeColors.buttonBgPrimary,
              color: themeColors.buttonTextPrimary,
              minWidth: "100px",
              "&:hover": {
                backgroundColor: themeColors.buttonBgHover
              }
            }}
          >
            {addLoading ? <CircularProgress size={20} sx={{ color: themeColors.buttonTextPrimary }} /> : "Add Event"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT EVENT MODAL */}
      <Dialog
        open={openEditModal}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setOpenEditModal(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: darkMode ? "0 20px 25px -5px rgba(0, 0, 0, 0.5)" : "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            transition: "all 0.3s ease"
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 700,
          fontSize: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${themeColors.borderColor}`,
          pb: 2,
          backgroundColor: themeColors.bgPrimary,
          color: themeColors.textPrimary,
          transition: "all 0.3s ease"
        }}>
          Edit Event Date & Time
          <IconButton
            onClick={() => setOpenEditModal(false)}
            disabled={editLoading}
            sx={{ color: themeColors.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: "16px",
          pt: 2.5,
          backgroundColor: themeColors.bgPrimary,
          transition: "all 0.3s ease"
        }}>
          {selectedEvent && (
            <div style={{
              backgroundColor: themeColors.bgInfoLight,
              padding: "14px",
              borderRadius: "10px",
              border: `1px solid ${themeColors.borderColor}`,
              transition: "all 0.3s ease"
            }}>
              <h3 style={{ 
                margin: 0, 
                color: themeColors.textPrimary,
                fontSize: "16px",
                fontWeight: 600
              }}>
                {selectedEvent.eventName}
              </h3>
              <p style={{ 
                margin: "4px 0 0 0", 
                fontSize: "13px", 
                color: themeColors.textSecondary 
              }}>
                {selectedEvent.venue}
              </p>
              <p style={{ 
                margin: "4px 0 0 0", 
                fontSize: "12px", 
                color: themeColors.textTertiary 
              }}>
                Current Date: {selectedEvent.date} | Current Time: {selectedEvent.time}
              </p>
            </div>
          )}

          <TextField
            label="Display Name (shown in app)"
            value={editFormData.displayName}
            onChange={(e) =>
              setEditFormData({
                ...editFormData,
                displayName: e.target.value
              })
            }
            fullWidth
            sx={{
              '& .MuiInputLabel-root': {
                color: themeColors.textLabel,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: themeColors.buttonBgPrimary,
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: themeColors.bgSecondary,
                '& fieldset': {
                  borderColor: themeColors.borderInput,
                },
                '&:hover fieldset': {
                  borderColor: themeColors.borderInput,
                },
                '&.Mui-focused fieldset': {
                  borderColor: themeColors.buttonBgPrimary,
                },
              },
              '& .MuiInputBase-input': {
                color: themeColors.textPrimary,
              },
            }}
          />
          
          <div style={{ display: "flex", gap: "12px" }}>
            <TextField
              label="New Event Date"
              type="date"
              value={editFormData.eventDate}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  eventDate: e.target.value
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: todayDate
              }}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  color: themeColors.textLabel,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: themeColors.buttonBgPrimary,
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: themeColors.bgSecondary,
                  '& fieldset': {
                    borderColor: themeColors.borderInput,
                  },
                  '&:hover fieldset': {
                    borderColor: themeColors.borderInput,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: themeColors.buttonBgPrimary,
                  },
                },
                '& .MuiInputBase-input': {
                  color: themeColors.textPrimary,
                },
              }}
            />

            <TextField
              label="New Event Time"
              type="time"
              value={editFormData.eventTime}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  eventTime: e.target.value
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: editFormData.eventDate === todayDate ? currentTime : undefined
              }}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  color: themeColors.textLabel,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: themeColors.buttonBgPrimary,
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: themeColors.bgSecondary,
                  '& fieldset': {
                    borderColor: themeColors.borderInput,
                  },
                  '&:hover fieldset': {
                    borderColor: themeColors.borderInput,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: themeColors.buttonBgPrimary,
                  },
                },
                '& .MuiInputBase-input': {
                  color: themeColors.textPrimary,
                },
              }}
            />
          </div>

          <div style={{
            backgroundColor: themeColors.bgInfoLight,
            padding: "12px",
            borderRadius: "8px",
            fontSize: "13px",
            color: themeColors.infoBoxText,
            border: `1px solid ${themeColors.borderColor}`,
            transition: "all 0.3s ease"
          }}>
            <strong>Note:</strong> Updated date/time will reflect everywhere this event appears in the dashboard, including all associated tickets.
          </div>
        </DialogContent>

        <DialogActions sx={{
          padding: "16px",
          gap: "10px",
          borderTop: `1px solid ${themeColors.borderColor}`,
          backgroundColor: themeColors.bgPrimary,
          transition: "all 0.3s ease"
        }}>
          <Button 
            onClick={() => setOpenEditModal(false)}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: themeColors.textSecondary,
              border: `1px solid ${themeColors.borderInput}`,
              "&:hover": {
                backgroundColor: themeColors.bgHover
              }
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editLoading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: themeColors.buttonBgPrimary,
              color: themeColors.buttonTextPrimary,
              minWidth: "100px",
              "&:hover": {
                backgroundColor: themeColors.buttonBgHover
              }
            }}
          >
            {editLoading ? <CircularProgress size={18} sx={{ color: themeColors.buttonTextPrimary }} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={darkMode ? "dark" : "light"}
      />
    </div>
  );
};
         
export default EventsUpdate;